import { createSlice } from "@reduxjs/toolkit";

let controlTimeout = null;

const initialState = {
  paused: false,
  showPreviewImages: false,
  showControls: true,
  currentArchive: null,
  nextArchive: null,
  archives: [],
};

const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    setPaused: (state, action) => {
      state.paused = action.payload;
    },
    setShowPreviewImages: (state, action) => {
      state.showPreviewImages = action.payload;
    },
    setShowControls: (state, action) => {
      state.showControls = action.payload;

      clearTimeout(controlTimeout);

      controlTimeout = setTimeout(() => {
        state.showControls = false;
      }, 2000);
    },
    setArchives: (state, action) => {
      const sortedArchives = [...action.payload].sort(
        (a, b) => a.start_ut - b.start_ut
      );
      state.archives = sortedArchives;
    },
    setCurrentArchive: (state, action) => {
      state.currentArchive = action.payload;

      const plainPayload = JSON.parse(JSON.stringify(action.payload));
      const plainArchives = JSON.parse(JSON.stringify(state.archives));

      plainArchives.sort((a, b) => {
        if (a.start_ut && b.start_ut) {
          return a.start_ut - b.start_ut;
        }
        return 0;
      });

      const currentTimestamp = Math.floor(Date.now() / 1000);

      const pastArchives = plainArchives.filter(
        (archive) => archive.stop_ut <= currentTimestamp
      );
      const currentAndFutureArchives = plainArchives.filter(
        (archive) => archive.stop_ut > currentTimestamp
      );

      // Find the current archive's index in the sorted array
      const currentIndex = plainArchives.findIndex((archive) => {
        // First try to match by ID if available
        if (archive.id && plainPayload.id) {
          return archive.id === plainPayload.id;
        }

        // Otherwise match by start/stop times (for EPG items)
        if (archive.start_ut && plainPayload.start_ut) {
          return (
            archive.start_ut === plainPayload.start_ut &&
            archive.stop_ut === plainPayload.stop_ut
          );
        }

        // If no match criteria found, this isn't the archive we're looking for
        return false;
      });

      if (currentIndex === -1) {
        state.nextArchive = null;
        return;
      }

      // Find the next archive in the chronological sequence
      const nextArchiveCandidate =
        currentIndex < plainArchives.length - 1
          ? plainArchives[currentIndex + 1]
          : null;

      if (!nextArchiveCandidate) {
        state.nextArchive = null;
        return;
      }

      const isNextArchivePast =
        nextArchiveCandidate.stop_ut <= currentTimestamp;
      const isNextArchiveCurrent =
        nextArchiveCandidate.start_ut <= currentTimestamp &&
        nextArchiveCandidate.stop_ut > currentTimestamp;
      const isNextArchiveFuture =
        nextArchiveCandidate.start_ut > currentTimestamp;

      if (isNextArchiveCurrent || isNextArchiveFuture) {
        state.nextArchive = null;
        return;
      }

      state.nextArchive = nextArchiveCandidate;
    },
  },
});

export const { setPaused } = playerSlice.actions;
export const { setShowPreviewImages } = playerSlice.actions;
export const { setShowControls } = playerSlice.actions;
export const { setArchives, setCurrentArchive } = playerSlice.actions;

export const selectIsPaused = (state) => state.player.paused;
export const selectShowPreviewImages = (state) =>
  state.player.showPreviewImages;
export const selectShowControls = (state) => state.player.showControls;
export const selectArchives = (state) => state.player.archives;
export const selectCurrentArchive = (state) => state.player.currentArchive;
export const selectNextArchive = (state) => state.player.nextArchive;

export default playerSlice.reducer;
